import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import PageNotificationHost from '../components/PageNotificationHost';

const PageNotificationContext = createContext(null);

/**
 * App-wide stacked toast (title + detail, hero icon). Use via usePageNotification().
 */
export function PageNotificationProvider({ children }) {
  const [notification, setNotification] = useState({
    open: false,
    title: '',
    message: '',
    severity: 'success'
  });

  const notify = useCallback((opts) => {
    if (!opts || typeof opts !== 'object') return;
    const { title = '', message = '', severity = 'success' } = opts;
    setNotification({
      open: true,
      title,
      message,
      severity: severity === 'error' ? 'error' : 'success'
    });
  }, []);

  const closeNotification = useCallback(() => {
    setNotification((n) => ({ ...n, open: false }));
  }, []);

  const value = useMemo(
    () => ({
      notify,
      closeNotification
    }),
    [notify, closeNotification]
  );

  return (
    <PageNotificationContext.Provider value={value}>
      {children}
      <PageNotificationHost notification={notification} onClose={closeNotification} />
    </PageNotificationContext.Provider>
  );
}

export function usePageNotification() {
  const ctx = useContext(PageNotificationContext);
  if (!ctx) {
    throw new Error('usePageNotification must be used within PageNotificationProvider');
  }
  return ctx;
}
